import {Injectable} from '@angular/core';
import {AuthenticationService} from '@services/authentification.service';
import {environment} from '~/environments/environment';
import {HttpClient} from '@angular/common/http';
import jwtDecode from 'jwt-decode';

declare const fbq: any;
declare const sendinblue: any;
declare const WonderPush: any;

// declare const gtag: any;
// declare const mixpanel: any;
declare global {
    interface Window {
        dataLayer: any;
        hj: any;
        WonderPush: any;
    }
}

// Déduplication de l'event subscribe : il part d'un ?success= présent dans l'URL, donc un
// rafraîchissement, un retour arrière ou une réouverture du lien le renverrait. On mémorise le
// dernier (compte, plan) envoyé ; au-delà du délai, un vrai ré-abonnement repasse.
const SUBSCRIBE_SENT_KEY = 'subscribeSent';
const SUBSCRIBE_SENT_DELAY = 15 * 24 * 60 * 60 * 1000; // 15 jours

@Injectable({
    providedIn: 'root'
})

export class TrackingService {

    constructor(
        public authenticationService: AuthenticationService,
        private http: HttpClient) {
    }

    initPixel() {
        if (this.authenticationService.isLogin) {
            const user = this.authenticationService.currentUser;
            if (typeof fbq !== 'undefined') {
                fbq('init', '738775470234919', {
                    em: user.email,
                    fn: user.firstname,
                    ln: user.lastname,
                    external_id: user.email
                });
            }

            var userId = user.id || null; // Replace your_user_id with your own if available.
            var plan = user.plan;
            if (user.pro_plan != '') {
                plan = user.pro_plan;
            }
            if (window.hj !== undefined) {
                window.hj('identify', userId, {
                    'email': user.email,
                    'created_at': user.created_at,
                    'plan': plan,
                    'identifiant': user.email.replace('@', '[arobase]'),
                    'rate': (user.rate_answer ? user.rate_answer.general : 0),
                    'type': (user.answer ? user.answer.profil : ''),
                });
            }
            if ( typeof sendinblue !== 'undefined') {
                sendinblue.identify(user.email);
            }

            if (window.WonderPush !== undefined) {
                window.WonderPush.push(['setUserId', user.id]);
            }
        }
    }

    login(params) {
        this.sendEvent('login', '', params);
        this.initPixel();
    }

    need2auth(params) {
        this.sendEvent('need2auth', '', params);
        this.initPixel();
    }

    search(params) {
        this.initPixel();
    }

    searchCity() {
        this.initPixel();
    }

    subscribe(params) {
        if (this.alreadySent(SUBSCRIBE_SENT_KEY, params && params.value)) {
            return;
        }
        this.sendEvent('subscribe', "", params);
        this.initPixel();
        if (window.dataLayer !== undefined) {
            window.dataLayer.push({'event': 'subscription_front'});
        }

        if (typeof sendinblue !== 'undefined') {
            setTimeout(function() {
                sendinblue.track(
                    'Subscribe', params, params
                );
            }.bind(params), 500);
        }
    }

    checkout(params) {
        // Départ vers Stripe : ce qui reviendra ensuite est une vraie souscription, pas un
        // rafraîchissement. On remet le compteur à zéro pour ne pas la rater.
        this.clearSent(SUBSCRIBE_SENT_KEY);
        this.sendEvent('checkout', "", params);
        this.initPixel();
    }

    register(method: 'classique' | 'google' | 'facebook') {
        this.sendEvent('register', "", method);
        this.initPixel();
        if (window.dataLayer !== undefined) {
            window.dataLayer.push({'event': 'completeRegistrationWelcome'});
            window.dataLayer.push({'event': 'inscription_' + method});
        }
        // if (typeof sendinblue !== 'undefined') {
        //     sendinblue.track(
        //         'CompleteRegistration'
        //     );
        // }
    }

    // Cancel subscription
    cancel(feadback, reason) {
        this.sendEvent('subscription_cancel', feadback, reason);
        this.initPixel();
        if (typeof sendinblue !== 'undefined') {
            sendinblue.track(
                'Unsubscribe'
            );
        }
    }

    // fail subscription
    subscribeFail() {
        this.sendEvent('subscription_fail', "", "");
        this.initPixel();
        if (typeof sendinblue !== 'undefined') {
            sendinblue.track(
                'CheckoutFail'
            );
        }
    }

    addFavorite() {
        this.sendEvent('favorite_add', "", "");
        this.initPixel();
    }

    addFavoriteList() {
        this.sendEvent('favorite_list_add', "", "");
        this.initPixel();
    }

    deleteFavoriteList() {
        this.sendEvent('favorite_list_delete', "", "");
        this.initPixel();
    }

    deleteFavorite() {
        this.sendEvent('favorite_delete', "", "");
        this.initPixel();
    }

    click(item, data) {
        this.sendEvent('click', item, data);
    }

    url(url) {
        this.sendEvent('navigate', '', url);
    }

    // true si le même event a déjà été envoyé pour ce compte et cette valeur il y a moins de
    // SUBSCRIBE_SENT_DELAY ; sinon mémorise l'envoi et renvoie false. localStorage indisponible
    // (navigation privée, stockage bloqué) : on laisse passer l'event plutôt que de le perdre.
    private alreadySent(storageKey: string, value: any): boolean {
        try {
            let userId = 0;
            const jwt = localStorage.getItem('token');
            if (jwt) {
                userId = jwtDecode(jwt)['id'];
            }
            const key = userId + ':' + String(value || '');
            const raw = localStorage.getItem(storageKey);
            const last = raw ? JSON.parse(raw) : null;
            if (last && last.key === key && Date.now() - last.at < SUBSCRIBE_SENT_DELAY) {
                return true;
            }
            localStorage.setItem(storageKey, JSON.stringify({key: key, at: Date.now()}));
        } catch (e) {
        }
        return false;
    }

    // Oublie le dernier envoi mémorisé : le prochain event repartira quoi qu'il arrive.
    private clearSent(storageKey: string) {
        try {
            localStorage.removeItem(storageKey);
        } catch (e) {
        }
    }

    // String({...}) donne "[object Object]" : les objets partent en JSON, le reste en texte.
    private serialize(data): string {
        if (data === null || data === undefined) {
            return '';
        }
        if (typeof data !== 'object') {
            return String(data);
        }
        try {
            return JSON.stringify(data);
        } catch (e) {
            return String(data);
        }
    }

    sendEvent(action, message, data) {
        let userId = 0;
        let uniqueId = '';

        let jwt = localStorage.getItem('token');
        if (jwt) {
            userId = jwtDecode(jwt)['id'];
        }

        if (!jwt || action == 'login') {
            let uId = localStorage.getItem('uId');
            if (!uId) {
                uniqueId = (Math.random() + 1).toString(36).substring(7) + '-' + (Math.random() + 1).toString(36).substring(7) + '-' + (Math.random() + 1).toString(36).substring(7);
                localStorage.setItem('uId', uniqueId);
            } else {
                uniqueId = uId;
            }
            if (action == 'login') {
                localStorage.setItem('uId', '');
            }
        }

        this.http.post(`${environment.apiUrl}app/log`, {
            action: action,
            message: String(message),
            userId: userId,
            uniqueId: uniqueId,
            data: this.serialize(data),
        }).subscribe(
            data => {
            },
            error => {
                console.log(error);
            });
    }
}
